import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core'
import { SsoService } from '../../../core/services/sso.service'
import { ConfirmationService } from 'primeng/api'
import { ClientGroup, UserSession } from '../../../models/user-session.model'
import { TableModule } from 'primeng/table'
import { ButtonModule } from 'primeng/button'
import { MessageModule } from 'primeng/message'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { LoggerService } from '@danangelus/ngx-app-core'
import { PaginatorModule, PaginatorState } from 'primeng/paginator'
import { TabsModule } from 'primeng/tabs'
import { SessionService } from "../../../core/security/session.service"
import { AuthenticationService } from "../../../core/security/authentication.service"
import { Skeleton } from "primeng/skeleton"

const log = LoggerService.get('ActiveSessionViewComponent')
/**
 * View of the active sessions in the system with the ability to end them remotely.
 *
 * @author Dan Bennett
 */
@Component({
    selector: 'app-active-session-view',
    standalone: true,
    imports: [
        ButtonModule,
        ConfirmDialogModule,
        MessageModule,
        PaginatorModule,
        Skeleton,
        TableModule,
        TabsModule,
    ],
    providers: [ConfirmationService],
    templateUrl: './active-session-view.component.html',
    styleUrl: './active-session-view.component.sass',
    encapsulation: ViewEncapsulation.None
})
export class ActiveSessionViewComponent implements OnInit {

    protected ssoService = inject(SsoService)
    confirmationService = inject(ConfirmationService)
    sessionService = inject(SessionService)
    authenticationService = inject(AuthenticationService)

    loading = true
    query = ''
    pageNumber = 1
    pageSize = 25
    totalRecords = 0
    firstRecord = 1
    total = 0

    tempSessions = [{},{},{},{},{}]
    activeSessions: UserSession[] = []
    groupedData: ClientGroup[] = []
    successfulResponse: boolean | undefined

    messageType = ''

    async ngOnInit()  {
        if (await this.sessionService.isSessionActive()) {
            this.loading = true
            await this.getActiveSessions()
            this.calculateClientGroups()
            this.totalRecords = this.activeSessions?.length || 0
            this.loading = false
        }
        else {
            log.debug('ngOnInit', 'User deactivated self. Logging current user out')
            await this.authenticationService.logout()
        }
    }

    private async getActiveSessions(): Promise<void> {
        let page = 0
        const activeSessions = []
        while(true) {
            const sessionPage = await this.ssoService.getActiveSessions(page, this.pageSize)
            if (sessionPage.activeSessions && sessionPage.activeSessions.length > 0) {
                activeSessions.push(...sessionPage.activeSessions)
                this.total = activeSessions.length
            }
            // If loading the first page, then reset the active sessions list
            if (page === 0) {
                this.activeSessions = activeSessions
            }
            if (!sessionPage.activeSessions || sessionPage.activeSessions.length < this.pageSize) {
                break
            }
            page += 1
        }
    }

    calculateClientGroups(): void {
        const totalSessions = this.activeSessions.length
        const clientCounts = this.activeSessions.reduce((acc, session) => {
            const client = session.client || 'UNKNOWN'
            acc[client] = (acc[client] || 0) + 1
            return acc
        }, {} as Record<string, number>)

        this.groupedData = Object.entries(clientCounts).map(([client, users]) => ({
            client,
            users,
            percentage: +(users / totalSessions * 100).toFixed(2)
        }))
    }

    async endUserSession(session: UserSession) {
        this.messageType = ''
        this.confirmationService.confirm({
            message: 'Are you sure you want to end this users current Session?',
            header: `End User Session: ${session.userId}`,
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                log.debug('endUserSession',`Deactivating user session with session id ${session.ssoTicketId}`)
                this.successfulResponse = await this.ssoService.deactivateSession(session.ssoTicketId!)
                await this.ngOnInit()
                if (this.successfulResponse) {
                    this.messageType = 'ENDED_SESSION'
                } else {
                    this.messageType = 'FAILED_TO_END_SESSION'
                }
            },
            reject: () => {
                this.messageType = 'ACTION_REJECTED'
            }
        })

    }
    async changePage(event: PaginatorState) {
        log.debug('changePage', `Finding active users ${this.query}`)
        this.pageNumber = event.page! + 1
        this.pageSize = event.rows!
        await this.getActiveSessions()
    }
}
